import { Button, ThemeToggle } from '@servicetim/ui/components';
import { cn, useConfig, useSession } from '@servicetim/ui/lib';
import { Link, useNavigate } from '@tanstack/react-router';
import { LogOut, Menu, ShieldOff, X } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useCanSeeOrganization } from '@/api';
import { useBrand } from '@/features/branding/brand-provider';
import { InstallHint } from '@/pwa/install-hint';
import { NAV_ITEMS } from '@/routes/nav';

/**
 * Tenant-branded shell: skip link, header with logo / portal name, top navigation (collapsed
 * behind a menu button below `lg` so the label row never wraps between 1024 and 1440px), user +
 * theme toggle + sign out, install hint.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const config = useConfig();
  const session = useSession();
  const brand = useBrand();
  const navigate = useNavigate();
  const canSeeOrganization = useCanSeeOrganization();
  const [navOpen, setNavOpen] = useState(false);
  const closeNav = () => setNavOpen(false);

  const userLabel = session.user?.name ?? session.user?.email ?? 'Signed in';
  const items = NAV_ITEMS.filter(
    (item) => !('gate' in item) || item.gate !== 'organization' || canSeeOrganization,
  );
  const isProd = config.environment === 'prod' || config.environment === 'production';
  const showPoweredBy = brand.tenantName !== brand.productName;

  return (
    <div className="flex min-h-dvh flex-col">
      <a
        href="#main"
        className="sr-only z-50 rounded-md bg-brand-600 px-3 py-2 text-white focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
      >
        Skip to main content
      </a>

      {session.authEnabled ? null : <AuthDisabledBanner />}
      <InstallHint />

      <header className="border-border border-b bg-surface-raised">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4">
          <Link
            to="/"
            className="flex min-w-0 shrink-0 items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={closeNav}
          >
            {brand.logoUrl ? (
              <img
                src={brand.logoUrl}
                alt=""
                className="size-9 shrink-0 rounded-md object-contain"
              />
            ) : (
              <span
                aria-hidden="true"
                className="grid size-9 shrink-0 place-items-center rounded-md bg-brand-600 font-bold text-white"
              >
                {brand.portalName.slice(0, 1).toUpperCase()}
              </span>
            )}
            <span className="flex max-w-[24ch] flex-col leading-tight lg:max-w-[36ch]">
              <span
                className="truncate font-semibold text-fg"
                data-testid="portal-name"
                title={brand.portalName}
              >
                {brand.portalName}
              </span>
              <span className="truncate text-fg-muted text-xs" title={brand.tenantName}>
                {brand.tenantName}
              </span>
            </span>
          </Link>
          {isProd ? null : (
            <span className="hidden shrink-0 rounded-full bg-surface-sunken px-2 py-0.5 font-medium text-fg-muted text-xs uppercase tracking-wide sm:inline-block">
              {config.environment}
            </span>
          )}

          <nav
            aria-label="Primary"
            className="hidden min-w-0 flex-1 items-center gap-1 overflow-x-auto lg:flex"
          >
            {items.map(({ label, icon: Icon, ...link }) => (
              <Link
                key={link.to}
                {...link}
                activeProps={{ 'aria-current': 'page' }}
                className={cn(
                  'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border-b-2 border-transparent px-2 py-2 font-medium text-fg-muted text-sm transition-colors xl:gap-2 xl:px-3',
                  'hover:bg-surface-sunken hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  'data-[status=active]:border-accent data-[status=active]:bg-accent-soft data-[status=active]:text-accent',
                )}
              >
                <Icon className="size-4 shrink-0" aria-hidden="true" />
                {label}
              </Link>
            ))}
          </nav>

          <div className="ml-auto flex shrink-0 items-center gap-2">
            {/* The signed-in address needs ~200 px; below 2xl it lives in the sign-out tooltip so
                the primary navigation never clips. */}
            <span
              className="hidden max-w-48 truncate text-fg-muted text-sm 2xl:inline"
              title={userLabel}
            >
              {userLabel}
            </span>
            <ThemeToggle className="hidden lg:inline-flex" />
            <Button
              variant="outline"
              size="sm"
              title={userLabel ? `Signed in as ${userLabel}` : undefined}
              onClick={() => void navigate({ to: '/signed-out' })}
            >
              <LogOut />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
              aria-expanded={navOpen}
              aria-controls="mobile-nav"
              onClick={() => setNavOpen((open) => !open)}
            >
              {navOpen ? <X /> : <Menu />}
            </Button>
          </div>
        </div>

        <nav
          id="mobile-nav"
          aria-label="Primary"
          hidden={!navOpen}
          className="border-border border-t bg-surface-raised lg:hidden"
        >
          <ul className="mx-auto max-w-7xl space-y-1 p-3">
            {items.map(({ label, icon: Icon, ...link }) => (
              <li key={link.to}>
                <Link
                  {...link}
                  onClick={closeNav}
                  activeProps={{ 'aria-current': 'page' }}
                  className={cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 font-medium text-fg-muted text-sm',
                    'hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    'data-[status=active]:bg-accent-soft data-[status=active]:text-accent',
                  )}
                >
                  <Icon className="size-4" aria-hidden="true" />
                  {label}
                </Link>
              </li>
            ))}
            <li className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="font-medium text-fg-muted text-sm">Colour mode</span>
              <ThemeToggle />
            </li>
          </ul>
        </nav>
      </header>

      <main id="main" tabIndex={-1} className="flex-1 px-4 py-6 outline-none md:py-10">
        {children}
      </main>

      <footer className="border-border border-t bg-surface-raised px-4 py-4 text-center text-fg-muted text-xs">
        {showPoweredBy
          ? `${brand.tenantName} - powered by ${brand.productName}`
          : brand.productName}
      </footer>
    </div>
  );
}

function AuthDisabledBanner() {
  return (
    <div
      role="status"
      className="flex items-center gap-2 border-amber-200 dark:border-amber-500/30 border-b bg-amber-50 dark:bg-amber-500/15 px-4 py-2 text-amber-900 dark:text-amber-300 text-sm"
    >
      <ShieldOff className="size-4 shrink-0" aria-hidden="true" />
      <span>
        <strong className="font-semibold">Auth disabled: local development mode.</strong> Requests
        are sent without an Authorization header. Set <code className="font-mono">cognito</code> or{' '}
        <code className="font-mono">devClaims</code> in{' '}
        <code className="font-mono">config.json</code> to sign in.
      </span>
    </div>
  );
}
